import * as React from "react";
import { useAsync } from "react-use";
import {
  GoogleMap,
  LoadScriptNext,
  Marker,
  InfoWindow,
  MarkerClusterer,
  MarkerProps,
  StandaloneSearchBox,
  useGoogleMap,
} from "@react-google-maps/api";
import { useEffect, useRef, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { TagFilter } from "./TagFilter";
import { VizProps } from "../VizPreview";
import { GroopitDetails } from "../Groopits/GroopitDetails";
import { useGroopitApiContext, VizDTO } from "groopit-js";
interface GroopItLocationModel extends google.maps.LatLngLiteral {
  Id: string;
  Tags: string[];
}

const containerStyle = {
  height: "550px",
};
const center = {
  lat: -28.024,
  lng: 140.887,
};

const fitBounds = (
  map: google.maps.Map,
  markers: google.maps.LatLngLiteral[],
): void => {
  const bounds = new window.google.maps.LatLngBounds();

  markers.forEach((marker) => bounds.extend(marker));
  map.fitBounds(bounds);
};

const clusterOptions = {
  imagePath: "/Scripts/markerclusterer/images/m", // so you must have m1.png, m2.png, m3.png, m4.png, m5.png and m6.png in that folder
};
interface MapControlProps {
  position: keyof typeof google.maps.ControlPosition;
}
const MapControl = (props: React.PropsWithChildren<MapControlProps>) => {
  const map = useGoogleMap();
  const ref = useRef(null);
  useEffect(() => {
    if (map && ref.current) {
      map.controls[window.google.maps.ControlPosition[props.position]].push(
        ref.current,
      );
    }
  }, [map, ref]);
  return <div ref={ref}>{props.children}</div>;
};

interface Props {
  locations: GroopItLocationModel[];
  assignment: string;
  showFilter: boolean;
}

export const MapViz = (props: Props): JSX.Element => {
  const { showFilter, locations } = { ...props };
  const [showSearch, setShowSearch] = useState(false);
  const [map, setMap] = useState<google.maps.Map>();
  const [searchBox, setSearchBox] = useState<google.maps.places.SearchBox>();
  const tags = Array.from(new Set(locations.flatMap((l) => l.Tags)))
    .sort()
    .map((t) => ({ label: t, active: true }));
  const [activeTags, setActiveTags] = useState(tags);

  const onMapLoad = (ref: google.maps.Map) => {
    setMap(ref);
    fitBounds(ref, locations);
  };

  const onSearchLoad = (ref: google.maps.places.SearchBox) => {
    setSearchBox(ref);
  };

  const onPlacesChanged = () => {
    const place = searchBox?.getPlaces()?.[0];
    if (place && map) {
      map.panTo(place.geometry?.location as google.maps.LatLng);
      if (place.geometry?.viewport) {
        map.fitBounds(place.geometry.viewport);
      }
    }
  };

  const onToggleTag = (tag: string) => {
    setActiveTags((activeTags) =>
      activeTags.map((t) =>
        t.label === tag ? { ...t, active: !t.active } : t,
      ),
    );
  };
  const onSelectAll = () => {
    setActiveTags(activeTags.map((t) => ({ ...t, active: true })));
  };

  return (
    <LoadScriptNext
      googleMapsApiKey="AIzaSyC3-ueMlWqw1L4L1hVGabSfID7Z8Fg52Z8"
      libraries={["places"]}
    >
      <>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={3}
          onLoad={onMapLoad}
        >
          <MarkerClusterer averageCenter={true} options={clusterOptions}>
            {(clusterer) => (
              <>
                {locations.map((l, i) => (
                  <GroopitMarker
                    key={i}
                    position={l}
                    clusterer={clusterer}
                    visible={
                      !showFilter ||
                      activeTags.some(
                        (t) => t.active && l.Tags?.includes(t.label),
                      )
                    }
                  />
                ))}
              </>
            )}
          </MarkerClusterer>
          <MapControl position="LEFT_BOTTOM">
            <div className="flex">
              <button
                className="mx-4"
                draggable="false"
                title="Change map location"
                aria-label="Change map location"
                type="button"
                onClick={() => setShowSearch(!showSearch)}
              >
                <span className="glyphicon glyphicon-screenshot text-lg" />
              </button>
              {showSearch && (
                <StandaloneSearchBox
                  onLoad={onSearchLoad}
                  onPlacesChanged={onPlacesChanged}
                >
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Change map location"
                  />
                </StandaloneSearchBox>
              )}
            </div>
          </MapControl>
        </GoogleMap>
        {showFilter && (
          <TagFilter
            tags={activeTags}
            onToggleTag={onToggleTag}
            onSelectAll={onSelectAll}
          />
        )}
      </>
    </LoadScriptNext>
  );
};

interface GroopitMarkerProps extends MarkerProps {
  position: GroopItLocationModel;
}

const GroopitMarker = (props: GroopitMarkerProps) => {
  const { position } = { ...props };
  const [open, setOpen] = useState(false);

  return (
    <Marker {...props} onClick={() => setOpen(!open)}>
      {open && (props.visible ?? true) && (
        <InfoWindow onCloseClick={() => setOpen(false)}>
          <GroopitDetails
            groopit_id={position.Id}
            is_popover={true}
          ></GroopitDetails>
        </InfoWindow>
      )}
    </Marker>
  );
};

export const MapVizPreview = (props: VizProps): JSX.Element => {
  const { viz } = useGroopitApiContext();

  const state = useAsync(async () => {
    return await viz.getDataPOST(new VizDTO({ title: "", ...props }));
  }, [props]);

  return (
    <div>
      {state.loading || !state.value ? (
        <CircularProgress />
      ) : (
        <MapViz {...state.value} />
      )}
    </div>
  );
};
